/** Copyright (c) 2019 T.Rowe Price. All rights reserved. */

import './StatementsPage.scss';

import { array, bool } from 'prop-types';
import React, { Fragment } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';

import { getQueryStringParams } from 'support/BrowserUtils';
import {
  getDocumentReferences,
  getCCPADocumentReferences,
  getCustomerMailingAddress,
  getCustomerResidentialAddress
} from 'services/Rest';
import { PageHeader } from '../Header/PageHeader';
import Time from 'services/Time';
import TimelyAlert from 'components/shared/TimelyAlert/TimelyAlert';
import LegalCopy from 'components/shared/LegalCopy/LegalCopy';
import StatementsTable from 'components/layouts/Statements/StatementsTable';
import MobileStatementsTable from 'components/layouts/Statements/MobileStatementsTable';
import StatementsFilters from 'components/layouts/Statements/StatementsFilters';
import MobileStatementsFilters from 'components/layouts/Statements/MobileStatementsFilters';
import HeaderIcon from 'components/shared/HeaderIcon/HeaderIcon';
import Toggle from 'components/shared/ToggleRP';
import { ReactComponent as Filter } from 'assets/filter.svg';
import Store from 'state/Store';
import { removeTimelyAlert } from 'state/timelyAlert/TimelyAlertActions';
import { getTimelyAlertsByPage } from 'state/timelyAlert/TimelyAlertSelectors';
import {
  getCcpaDocumentReferences,
  getFilteredDocumentReferences,
  getMobileFilterDocumentReferencesGroups,
  getStatementFilterState
} from 'state/statements/StatementsSelectors';
import { getRequestStatus } from 'state/requests/RequestStatusSelectors';
import { REQUEST_STATES } from 'state/requests/RequestStatusReducer';
import { getCustomerResidentialState } from 'state/customer/CustomerSelectors';

import {
  getView,
  has529Only,
  has529AppOnly,
  hasMaryland529Only,
  isDesktopLayout
} from 'state/view/ViewSelectors';
import Content from 'services/Content';
import { shouldFetchDocuments } from 'services/Statements';
import { residentialAddressEnabled } from 'services/FeatureFlags';
import CallToAction from 'components/layouts/CallToAction/CallToAction';
import TestAndTarget from 'components/shared/TestAndTarget/TestAndTarget';

const StatementsMbox = 'ret-aa-primary-page-alert';
const dataAtProperty = 'eb143c91-3999-92fd-60bc-2dca0b84690f';
const CTATarget = TestAndTarget(CallToAction, StatementsMbox, dataAtProperty);
const canViewStatementsTimelyAlert =
  Time.now < new Date(2021, 1, 1); /*show alert only until Jan 31st, 2021 */

class StatementsPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      fetchCcpa: false,
      docsFetched: false
    };
  }

  componentDidMount() {
    const {
      residentialState,
      statementsFilteringSelections: {
        timePeriod: { selection: yearSelection }
      },
      requestStatus: {
        documentReferences: docStatus,
        accounts: accountsStatus
      },
      accounts
    } = this.props;
    const { docsFetched } = this.state;

    const queryStringPairs = getQueryStringParams();
    const isTaxPageEntry = queryStringPairs.filter(
      (q) => q['taxPageEntry']
    ).length;

    if (
      shouldFetchDocuments(
        accountsStatus,
        docsFetched,
        docStatus,
        isTaxPageEntry
      )
    ) {
      getDocumentReferences(accounts, parseInt(yearSelection));
      this.setState({ docsFetched: true });
    }
    if(!residentialState) {
      if(residentialAddressEnabled()) {
        getCustomerResidentialAddress()
      } else {
        getCustomerMailingAddress();
      }
    }
  }

  componentDidUpdate(prevProps) {
    const {
      accounts,
      requestStatus: {
        ccpaDocumentReferences: ccpaStatus,
        customer: customerStatus,
        mailingAddress: addressStatus,
        residentialAddress: residentialAddressStatus,
        accounts: accountsStatus,
        documentReferences: docStatus
      },
      residentialState
    } = this.props;

    const { fetchCcpa, docsFetched } = this.state;

    const {
      statementsFilteringSelections: {
        timePeriod: { selection: prevYearSelection }
      }
    } = prevProps;

    const {
      statementsFilteringSelections: {
        timePeriod: { selection: yearSelection }
      }
    } = this.props;

    if (shouldFetchDocuments(accountsStatus, docsFetched, docStatus)) {
      getDocumentReferences(accounts, parseInt(yearSelection));
      this.setState({ docsFetched: true });
    }

    if (
      accountsStatus === REQUEST_STATES.received &&
      prevYearSelection != yearSelection
    ) {
      getDocumentReferences(accounts, parseInt(yearSelection));
    }

    if(residentialAddressEnabled()) {
      if (
        customerStatus === 'RECEIVED' &&
        residentialAddressStatus === 'RECEIVED' &&
        residentialState
      ) {
        if (residentialState === 'CA' && !ccpaStatus && !fetchCcpa) {
          this.setState({ fetchCcpa: true });
          getCCPADocumentReferences();
        }
      }
    } else {
      if (
        customerStatus === 'RECEIVED' &&
        addressStatus === 'RECEIVED' &&
        residentialState
      ) {
        if (residentialState === 'CA' && !ccpaStatus && !fetchCcpa) {
          this.setState({ fetchCcpa: true });
          getCCPADocumentReferences();
        }
      }
    }
  }

  render() {
    const {
      documents,
      has529Only,
      has529AppOnly,
      isDesktopLayout,
      mobileDocuments,
      timelyAlerts
    } = this.props;
    return (
      <Fragment>
        <CTATarget
          messageName={''}
          messageText={''}
          messageId={''}
        />
        <div className="statements-page">
          {isDesktopLayout ? (
            <Fragment>
              <PageHeader title="Statements, Confirmations, & Tax Forms" />

              {!has529AppOnly &&
                canViewStatementsTimelyAlert &&
                timelyAlerts.map(function (alert) {
                  return (
                    <TimelyAlert
                      key={alert.id}
                      id={alert.id}
                      messageText={alert.messageText}
                      hideTimelyAlert={(id) =>
                        Store.dispatch(removeTimelyAlert(id))
                      }
                    />
                  );
                })}
              <StatementsFilters />
              <StatementsTable rows={documents} />
            </Fragment>
          ) : (
            <Fragment>
              <Toggle>
                {({ on, setOn, setOff }) => (
                  <Fragment>
                    <div className="statements-page-title-section">
                      <PageHeader title="Statements, Confirmations, & Tax Forms">
                        <HeaderIcon
                          onClick={on ? setOff : setOn}
                          icon={Filter}
                          label="Filter"
                          classPrefix="statements-page"
                        />
                      </PageHeader>
                    </div>
                    {!has529AppOnly &&
                      canViewStatementsTimelyAlert &&
                      timelyAlerts.map(function (alert) {
                        return (
                          <TimelyAlert
                            key={alert.id}
                            id={alert.id}
                            messageText={alert.messageText}
                            hideTimelyAlert={(id) =>
                              Store.dispatch(removeTimelyAlert(id))
                            }
                          />
                        );
                      })}

                    {on && (
                      <MobileStatementsFilters close={setOff} isOpen={on} />
                    )}
                  </Fragment>
                )}
              </Toggle>

              <MobileStatementsTable dateGroups={mobileDocuments} />
            </Fragment>
          )}
          {has529Only ? (
            <LegalCopy content={Content.statementsDisclosure529Only} />
          ) : (
            <LegalCopy content={Content.statementsDisclosure} />
          )}
          <LegalCopy content={Content.statementsPdf} />
        </div>
      </Fragment>
    );
  }
}

StatementsPage.propTypes = {
  accounts: array.isRequired,
  documents: array.isRequired,
  isDesktopLayout: bool.isRequired,
  mobileDocuments: array.isRequired,
  timelyAlerts: array
};

const mapStateToProps = (state) => ({
  accounts: state.accounts,
  ccpaDocumentReferences: getCcpaDocumentReferences(state),
  documents: getFilteredDocumentReferences(state),
  mobileDocuments: getMobileFilterDocumentReferencesGroups(state),
  isDesktopLayout: isDesktopLayout(state),
  has529Only: has529Only(state),
  has529AppOnly: has529AppOnly(state),
  hasMaryland529Only: hasMaryland529Only(state),
  view: getView(state),
  requestStatus: getRequestStatus(state),
  residentialState: getCustomerResidentialState(state),
  statementsFilteringSelections: getStatementFilterState(state),
  timelyAlerts: getTimelyAlertsByPage(Store.getState())
});

export default withRouter(connect(mapStateToProps)(StatementsPage));
